import React, { useState, useEffect, useContext } from 'react';

import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';

import PlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';

import {
  Box,
  Typography,
  IconButton,
  Stack,
  Modal,
  FormControl,
  Button,
  TextField,
  Chip,
  List,
  ListItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import { LoadingButton } from '@mui/lab';
import DirectionMapView from '../../DirectionMapView';
import { CreateNewRoute, UpdteRouteDetailsById } from '../../../network/Routes';

// ----------------------------------------------------------------------

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', md: '50%' },
  maxWidth: { xs: '70%', md: '50%' },
  bgcolor: 'background.paper',
  borderRadius: 1,
  maxHeight: '80vh',
  overflowY: 'auto',
  minHeight: '70vh',
  height: '100%'
};

const searchOptions = {
  componentRestrictions: { country: 'in' },
  fields: ['address_components', 'geometry', 'icon', 'name']
};

const RouteSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
  distance: Yup.string().required('Required'),
  eta: Yup.string().required('Required'),
  startLocation: Yup.string().required('Required'),
  endLocation: Yup.string().required('Required')
});

export default function AddNewRouteModal({
  openAssignedModal,
  setIsAddRouteModalOpen,
  onAction,
  data,
  adhocRouteData,
  setSelectedRouteData
}) {
  const [directions, setDirections] = useState(null);
  const [nextPageModal, setNextPageModal] = useState(true);

  const addNewRouteFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || '',
      description: data?.description || '',
      distance: data?.distance || '',
      eta: data?.eta || '',
      startLocation: data?.startLocation.address || '',
      endLocation: data?.endLocation.address || '',
      startLocationLatLng: data
        ? {
            location: data.startLocation.location,
            address: data?.startLocation?.address,
            state: data?.startLocation?.state,
            country: data?.startLocation?.country,
            pincode: data?.startLocation?.pincode
          }
        : '',
      endLocationLatLng: data
        ? {
            location: data.endLocation.location,
            address: data?.endLocation?.address,
            state: data?.endLocation?.state,
            country: data?.endLocation?.country,
            pincode: data?.endLocation?.pincode
          }
        : '',
      waypoints: data?.waypoints?.map((wp) => wp?.address) || [],
      waypointLatLng: data?.id
        ? data.waypoints.map((points) => ({
            address: points.address,
            country: points.country,
            state: points.state,
            pincode: points.pincode,
            location: points.location
          }))
        : []
    },
    validationSchema: RouteSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        name: values?.name,
        description: values?.description,
        eta: values?.eta,
        distance: values?.distance,
        startLocation: values?.startLocationLatLng,
        endLocation: values?.endLocationLatLng
      };
      if (values?.waypointLatLng?.length > 0) {
        payload.waypoints = values.waypointLatLng;
      }

      addNewRouteFormik.isSubmitting = true;

      addRoute(payload);
      resetForm();
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } =
    addNewRouteFormik;

  const handleClose = () => {
    if (setSelectedRouteData) {
      setSelectedRouteData(null);
    }
    setIsAddRouteModalOpen(false);
    addNewRouteFormik.resetForm();
  };

  const handleStartLocationChange = (value) => {
    setFieldValue('startLocation', value);
  };

  const handleEndLocationChange = (value) => {
    setFieldValue('endLocation', value);
  };

  const handleStartLocationSelect = async (selectedAddress, placeId, suggestion) => {
    try {
      let places = null;

      await geocodeByPlaceId(placeId)
        .then((results) => (places = results))
        .catch((error) => console.error(error));

      if (places !== null) {
        const address = places[0].address_components;

        let city = '';
        let state = '';
        let country = '';
        let pincode = '';

        address.forEach((component) => {
          const { types } = component;
          if (types.includes('locality')) city = component.long_name;
          if (types.includes('administrative_area_level_1')) state = component.long_name;
          if (types.includes('country')) country = component.long_name;
          if (types.includes('postal_code')) pincode = component.long_name;
        });

        // Prompt user if pincode is missing
        if (!pincode) {
          pincode = prompt('Postal code not found. Please enter the postal code:');
          if (!pincode) {
            onAction('Postal code is required. Please try again.', 'MESSAGE');
            return;
          }
        }

        // Get Latitude and Longitude
        const latLng = await getLatLng(places[0]);

        // Update Formik's state
        setFieldValue('startLocation', selectedAddress);
        setFieldValue('startLocationLatLng', {
          latlng: latLng,
          location: `${latLng.lat},${latLng.lng}`,
          address: selectedAddress,
          city,
          state,
          country,
          pincode
        });
      } else {
        onAction('Oops, something went wrong, please try again!', 'MESSAGE');
      }
    } catch (error) {
      console.error('Error while geocoding:', error);
    }
  };

  const handleEndLocationSelect = async (selectedAddress, placeId, suggestion) => {
    try {
      let places = null;

      await geocodeByPlaceId(placeId)
        .then((results) => (places = results))
        .catch((error) => console.error(error));

      if (places !== null) {
        const address = places[0].address_components;

        let city = '';
        let state = '';
        let country = '';
        let pincode = '';

        address.forEach((component) => {
          const { types } = component;
          if (types.includes('locality')) city = component.long_name;
          if (types.includes('administrative_area_level_1')) state = component.long_name;
          if (types.includes('country')) country = component.long_name;
          if (types.includes('postal_code')) pincode = component.long_name;
        });

        // Prompt user if pincode is missing
        if (!pincode) {
          pincode = prompt('Postal code not found. Please enter the postal code:');
          if (!pincode) {
            onAction('Postal code is required. Please try again.', 'MESSAGE');
            return;
          }
        }

        // Get Latitude and Longitude
        const latLng = await getLatLng(places[0]);

        // Update Formik's state
        setFieldValue('endLocation', selectedAddress);
        setFieldValue('endLocationLatLng', {
          latlng: latLng,
          location: `${latLng.lat},${latLng.lng}`,
          address: selectedAddress,
          city,
          state,
          country,
          pincode
        });
      } else {
        onAction('Oops, something went wrong, please try again!', 'MESSAGE');
      }
    } catch (error) {
      console.error('Error while geocoding:', error);
    }
  };

  const handleAddWaypoint = (event) => {
    event.preventDefault();

    // Ensure all existing waypoints are selected before adding a new one
    if (!validateWaypoints()) {
      onAction('Please complete all existing waypoints before adding a new one.', 'MESSAGE');
      return;
    }

    // Restrict waypoints to a maximum of 3
    if (addNewRouteFormik.values.waypoints.length >= 3) {
      onAction("You can't add more than 3 waypoints.", 'MESSAGE');
      return;
    }

    // Add an empty waypoint input field
    addNewRouteFormik.setFieldValue('waypoints', [...values.waypoints, '']);

    // Add an empty waypointLatLng object
    addNewRouteFormik.setFieldValue('waypointLatLng', [
      ...addNewRouteFormik.values.waypointLatLng,
      {}
    ]);
  };

  const handleWaypointChange = (value, index) => {
    const updatedWaypoints = [...values.waypoints];
    updatedWaypoints[index] = value;
    setFieldValue('waypoints', updatedWaypoints);
  };

  const handleWaypointLocationSelect = async (selectedAddress, index, placeId) => {
    try {
      let places = null;

      await geocodeByPlaceId(placeId)
        .then((results) => (places = results))
        .catch((error) => console.error(error));

      if (!places) {
        onAction('Oops, something went wrong, please try again!', 'MESSAGE');
        return;
      }

      const address = places[0].address_components;
      let city = '';
      let state = '';
      let country = '';
      let pincode = '';

      address.forEach((component) => {
        if (component.types.includes('locality')) city = component.long_name;
        if (component.types.includes('administrative_area_level_1')) state = component.long_name;
        if (component.types.includes('country')) country = component.long_name;
        if (component.types.includes('postal_code')) pincode = component.long_name;
      });

      if (!pincode) {
        pincode = prompt('Postal code not found. Please enter the postal code:');
        if (!pincode) {
          onAction('Postal code is required. Please try again.', 'MESSAGE');
          return;
        }
      }

      const latLng = await getLatLng(places[0]);

      // Ensure waypoints array exists before updating
      const updatedWaypoints = [...values.waypoints];
      updatedWaypoints[index] = selectedAddress;
      setFieldValue('waypoints', updatedWaypoints);

      // Ensure waypointLatLng array exists before updating
      const updatedWaypointsLatLng = [...values.waypointLatLng];
      updatedWaypointsLatLng[index] = {
        latlng: latLng,
        location: `${latLng.lat},${latLng.lng}`,
        address: selectedAddress,
        city,
        state,
        country,
        pincode
      };
      setFieldValue('waypointLatLng', updatedWaypointsLatLng);
    } catch (error) {
      console.error('Error while geocoding:', error);
    }
  };

  const validateWaypoints = () =>
    !values.waypointLatLng.some(
      (waypoint) =>
        !waypoint?.location ||
        !waypoint?.address ||
        !waypoint?.state ||
        !waypoint?.country ||
        !waypoint?.pincode
    );

  const handleRemoveWaypoint = (index) => {
    const updatedWaypoints = addNewRouteFormik.values.waypoints.filter((_, i) => i !== index);
    const updatedWaypointLatLng = addNewRouteFormik.values.waypointLatLng.filter(
      (_, i) => i !== index
    );

    addNewRouteFormik.setFieldValue('waypoints', updatedWaypoints);
    addNewRouteFormik.setFieldValue('waypointLatLng', updatedWaypointLatLng);
  };

  const addRoute = async (payload) => {
    try {
      let response;

      if (data?.id) {
        response = await UpdteRouteDetailsById(data?.id, payload);
      } else {
        response = await CreateNewRoute(payload);
      }

      if (response?.data?.data) {
        if (adhocRouteData) {
          adhocRouteData(response?.data?.data?.id, response?.data?.data?.name); // For adhoc route while adding trip
        }

        onAction(
          data?.id ? 'Route has been updated successfully' : 'Route has been added successfully',
          'SUCCESS'
        );
      } else {
        throw new Error('Empty response from server');
      }
    } catch (error) {
      console.error('Error while adding/updating route:', error);
      onAction(
        error?.response?.data?.message ||
          error.message ||
          'An error occurred while adding the route',
        'MESSAGE'
      );
    } finally {
      handleClose();
    }
  };

  useEffect(() => {
    if (values.startLocationLatLng.length !== 0 && values.endLocationLatLng.length !== 0) {
      try {
        setDirections(null);
        const directionsServiceOptions = {
          destination: values.endLocationLatLng?.latlng,
          origin: values.startLocationLatLng?.latlng,
          waypoints: values.waypointLatLng?.map((waypointLatLng) => ({
            location: waypointLatLng?.latlng,
            stopover: true
          })),
          travelMode: 'DRIVING'
        };

        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(directionsServiceOptions, (response, status) => {
          if (status === 'OK') {
            setDirections(response);
          } else {
            console.error('Directions request failed with status:', status);
          }
        });
      } catch (error) {
        console.error('Error calculating route:', error);
      }
    }
  }, [values?.startLocationLatLng, values?.endLocationLatLng, values?.waypointLatLng]);
  useEffect(() => {
    if (directions) {
      const etaValue = directions?.routes?.reduce(
        (acc, route) =>
          acc +
          (route?.legs?.reduce((legAcc, leg) => legAcc + (leg?.duration?.value || 0), 0) || 0),
        0
      );

      const distanceValue = directions?.routes?.reduce(
        (acc, route) =>
          acc +
          (route?.legs?.reduce((legAcc, leg) => legAcc + (leg?.distance?.value || 0), 0) || 0),
        0
      );

      setFieldValue('eta', etaValue || 0);
      setFieldValue('distance', distanceValue || 0);
    }
  }, [directions]);

  return (
    <Modal
      open={openAssignedModal}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      data-testid="add-new-route-modal"
    >
      <FormikProvider value={addNewRouteFormik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box sx={style}>
            <Box display="flex" width="100%" height="100%">
              {/* Left Panel */}
              {nextPageModal && (
                <Box
                  sx={{
                    width: { xs: '100%', sm: '50%' },
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 900, fontStyle: 'bold', mb: 2 }}
                  >
                    Add Route
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <PlacesAutocomplete
                      value={values.startLocation} // Use Formik's values
                      searchOptions={searchOptions}
                      onChange={(value) => setFieldValue('startLocation', value)}
                      onSelect={(address, placeId, suggestion) =>
                        handleStartLocationSelect(address, placeId, suggestion)
                      }
                    >
                      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                        <div>
                          <TextField
                            fullWidth
                            label="Start Location *"
                            id="startLocation"
                            name="startLocation"
                            autoComplete="off"
                            variant="outlined"
                            {...getInputProps()} // Get input props from PlacesAutocomplete
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: { xs: '12px', sm: '14px' },
                                fontWeight: 500
                              },
                              '& .MuiOutlinedInput-input': {
                                fontSize: { xs: '12px', sm: '14px' },
                                padding: { xs: '14px 8px', sm: '16px' }
                              },
                              '& .MuiFormHelperText-root': {
                                fontSize: { xs: '10px', sm: '12px' }
                              }
                            }}
                            error={Boolean(touched.startLocation && errors.startLocation)}
                            helperText={touched.startLocation && errors.startLocation}
                          />
                          {/* Suggestions List */}
                          <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                            {loading && (
                              <Typography sx={{ fontSize: '12px' }}>Loading...</Typography>
                            )}
                            {suggestions.map((suggestion, index) => {
                              const style = {
                                backgroundColor: suggestion.active ? '#f2f2f2' : '#ffffff',
                                cursor: 'pointer'
                              };
                              return (
                                <Box
                                  key={index}
                                  sx={{ p: 1 }}
                                  {...getSuggestionItemProps(suggestion, { style })}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <FmdGoodOutlinedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    <Typography sx={{ fontSize: 14 }}>
                                      {suggestion.description}
                                    </Typography>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Box>
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </FormControl>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#7c7d7d',
                      pl: 1,
                      mb: 1
                    }}
                  >
                    Waypoints
                    <IconButton aria-label="add" onClick={(e) => handleAddWaypoint(e)}>
                      <AddIcon sx={{ color: '#171D40', fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {values?.waypoints?.map((waypoint, index) => (
                    <FormControl key={index} fullWidth sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                        <PlacesAutocomplete
                          value={waypoint}
                          searchOptions={searchOptions}
                          onChange={(value) => handleWaypointChange(value, index)}
                          onSelect={(address, placeId) =>
                            handleWaypointLocationSelect(address, index, placeId)
                          }
                        >
                          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                            <Box sx={{ width: '100%' }}>
                              <TextField
                                fullWidth
                                label={`Waypoint ${index + 1}*`}
                                autoComplete="off"
                                variant="outlined"
                                {...getInputProps()} // Attach PlacesAutocomplete input props
                                sx={{
                                  '& .MuiInputLabel-root': {
                                    fontSize: { xs: '12px', sm: '14px' },
                                    fontWeight: 500
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: { xs: '12px', sm: '14px' },
                                    padding: { xs: '14px 8px', sm: '16px' }
                                  },
                                  '& .MuiFormHelperText-root': {
                                    fontSize: { xs: '10px', sm: '12px' }
                                  }
                                }}
                                error={Boolean(
                                  addNewRouteFormik.touched.waypoints &&
                                    addNewRouteFormik.errors.waypoints
                                )}
                                helperText={
                                  addNewRouteFormik.touched.waypoints &&
                                  addNewRouteFormik.errors.waypoints
                                }
                              />

                              {/* Suggestions List */}
                              <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                {loading && (
                                  <Typography sx={{ fontSize: '12px' }}>Loading...</Typography>
                                )}
                                {suggestions.map((suggestion, i) => {
                                  const style = {
                                    backgroundColor: suggestion.active ? '#f2f2f2' : '#ffffff',
                                    cursor: 'pointer'
                                  };
                                  return (
                                    <Box
                                      key={i}
                                      sx={{ p: 1 }}
                                      {...getSuggestionItemProps(suggestion, { style })}
                                    >
                                      <Stack
                                        direction="row"
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        spacing={1}
                                      >
                                        <FmdGoodOutlinedIcon
                                          sx={{ fontSize: { xs: 14, sm: 16 } }}
                                        />
                                        <Typography sx={{ fontSize: 14 }}>
                                          {suggestion.description}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </PlacesAutocomplete>
                        <IconButton
                          aria-label="remove"
                          sx={{ color: 'red' }}
                          onClick={() => handleRemoveWaypoint(index)}
                        >
                          <RemoveOutlinedIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                      </Stack>
                    </FormControl>
                  ))}

                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <PlacesAutocomplete
                      value={values.endLocation} // Use Formik's values
                      searchOptions={searchOptions}
                      onChange={(value) => handleEndLocationChange(value)}
                      onSelect={(address, placeId, suggestion) =>
                        handleEndLocationSelect(address, placeId, suggestion)
                      }
                    >
                      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                        <div>
                          <TextField
                            fullWidth
                            label="End Location *"
                            id="endLocation"
                            name="endLocation"
                            autoComplete="off"
                            variant="outlined"
                            {...getInputProps()} // Get input props from PlacesAutocomplete
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: { xs: '12px', sm: '14px' },
                                fontWeight: 500
                              },
                              '& .MuiOutlinedInput-input': {
                                fontSize: { xs: '12px', sm: '14px' },
                                padding: { xs: '14px 8px', sm: '16px' }
                              },
                              '& .MuiFormHelperText-root': {
                                fontSize: { xs: '10px', sm: '12px' }
                              }
                            }}
                            error={Boolean(touched.endLocation && errors.endLocation)}
                            helperText={touched.endLocation && errors.endLocation}
                          />
                          {/* Suggestions List */}
                          <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                            {loading && (
                              <Typography sx={{ fontSize: '12px' }}>Loading...</Typography>
                            )}
                            {suggestions.map((suggestion, index) => {
                              const style = {
                                backgroundColor: suggestion.active ? '#f2f2f2' : '#ffffff',
                                cursor: 'pointer'
                              };
                              return (
                                <Box
                                  key={index}
                                  sx={{ p: 1 }}
                                  {...getSuggestionItemProps(suggestion, { style })}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <FmdGoodOutlinedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    <Typography sx={{ fontSize: 14 }}>
                                      {suggestion.description}
                                    </Typography>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Box>
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </FormControl>
                  {values.distance.length !== 0 && values.eta.length !== 0 && (
                    <Stack marginTop="auto" display="flex" flexDirection="column" gap={2}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#7c7d7d' }}>
                        Total Distance -{' '}
                        <span style={{ color: '#171D40' }}>
                          {values.distance ? `${Math.round(values.distance / 1000)} Km` : '--'}
                        </span>
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#7c7d7d' }}>
                        ETA -{' '}
                        <span style={{ color: '#171D40' }}>
                          {values.eta
                            ? (() => {
                                const hours = Math.floor(values.eta / 3600);
                                const minutes = Math.floor((values.eta % 3600) / 60);
                                return `${hours} hrs ${minutes} mins`;
                              })()
                            : '--'}
                        </span>
                      </Typography>
                    </Stack>
                  )}
                  <Box mt={2} display="flex" justifyContent="space-between" marginTop="auto">
                    <Button variant="outlined" size="small" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => setNextPageModal(false)}
                      disabled={
                        !addNewRouteFormik.values.startLocation.trim() ||
                        !addNewRouteFormik.values.endLocation.trim()
                      }
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
              {!nextPageModal && (
                <Box
                  sx={{
                    width: { xs: '100%', sm: '50%' },
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 900, fontStyle: 'bold', mb: 2 }}
                  >
                    Confirm Route
                  </Typography>
                  <Stack display="flex" flexDirection="column" gap={1}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#7c7d7d'
                      }}
                    >
                      Start Location -{' '}
                      <span style={{ color: '#171D40' }}>
                        {values.startLocation ? values.startLocation : '--'}
                      </span>
                    </Typography>
                    {values.waypoints.length !== 0 && (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#7c7d7d'
                        }}
                      >
                        Waypoints -{' '}
                        <span style={{ color: '#171D40' }}>
                          {values.waypoints.map((item, index) => (
                            <List key={index}>
                              <ListItem>{item}</ListItem>
                            </List>
                            // <ul key={index}>
                            //   <li>{item}</li>
                            // </ul>
                          ))}
                        </span>
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#7c7d7d'
                      }}
                    >
                      End Location -{' '}
                      <span style={{ color: '#171D40' }}>
                        {values.endLocation ? values.endLocation : '--'}
                      </span>
                    </Typography>
                  </Stack>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    marginTop="auto"
                  >
                    <Stack display="flex" flexDirection="column" gap={1}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#7c7d7d' }}>
                        Total Distance -{' '}
                        <span style={{ color: '#171D40' }}>
                          {values.distance ? `${Math.round(values.distance / 1000)} Km` : '--'}
                        </span>
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#7c7d7d' }}>
                        ETA -{' '}
                        <span style={{ color: '#171D40' }}>
                          {values.eta
                            ? (() => {
                                const hours = Math.floor(values.eta / 3600);
                                const minutes = Math.floor((values.eta % 3600) / 60);
                                return `${hours} hrs ${minutes} mins`;
                              })()
                            : '--'}
                        </span>
                      </Typography>
                    </Stack>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setNextPageModal(true)}
                    >
                      Edit Route
                    </Button>
                  </Box>

                  <Box mt="auto" display="flex" flexDirection="column">
                    <FormControl fullWidth sx={{ mb: 3, mt: 'auto' }}>
                      <TextField
                        label="Route Name *"
                        id="name"
                        name="name"
                        autoComplete="off"
                        variant="outlined"
                        {...getFieldProps('name')}
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '12px', sm: '14px' },
                            fontWeight: 500
                          },
                          '& .MuiOutlinedInput-input': {
                            fontSize: { xs: '12px', sm: '14px' },
                            padding: { xs: '14px 8px', sm: '16px' }
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: { xs: '10px', sm: '12px' }
                          }
                        }}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </FormControl>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      marginTop="auto"
                    >
                      <Button variant="outlined" onClick={handleClose} size="small">
                        Cancel
                      </Button>
                      <Box>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => setNextPageModal(true)}
                          sx={{ mr: 1 }}
                        >
                          Back
                        </Button>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          size="small"
                          type="submit"
                          loading={isSubmitting}
                          disabled={!addNewRouteFormik.values.name.trim()}
                        >
                          {data?.id ? 'Save Changes' : 'Create Route'}
                        </LoadingButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Right Panel (Google Map) */}
              <Box
                sx={{
                  width: { xs: '100%', sm: '50%' } // Full width on extra small screens, 75% on small screens, and 50% on medium+
                }}
              >
                <DirectionMapView direction={directions} />
              </Box>
            </Box>
          </Box>
        </Form>
      </FormikProvider>
    </Modal>
  );
}
